import { useState } from 'react'
import { MapPin, Save, Trash2, Edit2, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { localizeApiDetail } from '../i18n/apiError'
import { useMineMarkedsomrader } from '../hooks/useMineMarkedsomrader'
import { useActiveMarkedsomrade } from '../hooks/useActiveMarkedsomrade'
import { userAPI, type Markedsomrade } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import ErrorMessage from './ErrorMessage'

function validatePostnumre(input: string): { ok: true; codes: string[] } | { ok: false; error: string } {
  const codes = input.split(',').map(c => c.trim()).filter(Boolean)
  if (codes.length === 0) return { ok: false, error: i18n.t('components:mineMarkedsomrader.minOne') }
  for (const c of codes) {
    if (!/^\d{4}$/.test(c)) return { ok: false, error: i18n.t('components:mineMarkedsomrader.invalidPostnummer', { code: c }) }
  }
  return { ok: true, codes }
}

interface Props {
  onToast: (msg: string, type: 'success' | 'error') => void
}

export default function MineMarkedsomraderSection({ onToast }: Props) {
  const { t } = useTranslation('components')
  const { items, loading, error, refresh } = useMineMarkedsomrader()
  const { activeId, setActiveId, clearActive } = useActiveMarkedsomrade()

  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftPostnumre, setDraftPostnumre] = useState('')
  const [saving, setSaving] = useState(false)

  const startCreate = () => {
    setEditingId('new'); setDraftName(''); setDraftPostnumre('')
  }
  const startEdit = (it: Markedsomrade) => {
    setEditingId(it.id); setDraftName(it.name); setDraftPostnumre(it.postnumre.join(','))
  }
  const cancelEdit = () => {
    setEditingId(null); setDraftName(''); setDraftPostnumre('')
  }

  const handleSave = async () => {
    if (!draftName.trim()) return onToast(t('mineMarkedsomrader.nameRequired'), 'error')
    const v = validatePostnumre(draftPostnumre)
    if (!v.ok) return onToast(v.error, 'error')
    try {
      setSaving(true)
      if (editingId === 'new') {
        await userAPI.createMarkedsomrade(draftName.trim(), v.codes)
        onToast(t('mineMarkedsomrader.created'), 'success')
      } else if (typeof editingId === 'number') {
        await userAPI.updateMarkedsomrade(editingId, draftName.trim(), v.codes)
        onToast(t('mineMarkedsomrader.updated'), 'success')
      }
      cancelEdit()
      await refresh()
    } catch (err: any) {
      onToast(localizeApiDetail(err?.response?.data?.detail) || t('mineMarkedsomrader.saveError'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm(t('mineMarkedsomrader.confirmDelete'))) return
    try {
      setSaving(true)
      await userAPI.deleteMarkedsomrade(id)
      if (activeId === id) clearActive()
      onToast(t('mineMarkedsomrader.deleted'), 'success')
      await refresh()
    } catch (err: any) {
      onToast(localizeApiDetail(err?.response?.data?.detail) || t('mineMarkedsomrader.deleteError'), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card p-6" data-testid="mine-markedsomrader-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <MapPin className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('mineMarkedsomrader.title')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('mineMarkedsomrader.subtitle')}
            </p>
          </div>
        </div>
        {editingId === null && (
          <button onClick={startCreate} className="btn-secondary flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t('mineMarkedsomrader.add')}
          </button>
        )}
      </div>

      {loading ? <LoadingSpinner /> : error ? <ErrorMessage message={error} /> : (
        <div className="space-y-3">
          {items.length === 0 && editingId !== 'new' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('mineMarkedsomrader.empty')}
            </p>
          )}

          {items.map(it => editingId === it.id ? (
            <EditRow key={it.id}
              name={draftName} setName={setDraftName}
              postnumre={draftPostnumre} setPostnumre={setDraftPostnumre}
              saving={saving} onSave={handleSave} onCancel={cancelEdit} />
          ) : (
            <div key={it.id} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-dark-border rounded-lg">
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {it.name}
                  {activeId === it.id && (
                    <span className="ml-2 text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-sm">
                      {t('mineMarkedsomrader.active')}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('mineMarkedsomrader.postnummerList', { n: it.postnumre.length, list: it.postnumre.slice(0, 8).join(', ') })}
                  {it.postnumre.length > 8 && ` +${it.postnumre.length - 8}`}
                </div>
              </div>
              <button onClick={() => activeId === it.id ? clearActive() : setActiveId(it.id)}
                      className="btn-secondary text-xs"
                      title={activeId === it.id ? t('mineMarkedsomrader.deactivate') : t('mineMarkedsomrader.activate')}>
                {activeId === it.id ? t('mineMarkedsomrader.deactivate') : t('mineMarkedsomrader.activate')}
              </button>
              <button onClick={() => startEdit(it)} className="btn-secondary" title={t('mineMarkedsomrader.edit')}>
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => handleDelete(it.id)} disabled={saving}
                      className="btn-secondary text-red-600 dark:text-red-400" title={t('mineMarkedsomrader.delete')}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          {editingId === 'new' && (
            <EditRow
              name={draftName} setName={setDraftName}
              postnumre={draftPostnumre} setPostnumre={setDraftPostnumre}
              saving={saving} onSave={handleSave} onCancel={cancelEdit} />
          )}
        </div>
      )}
    </div>
  )
}

interface EditRowProps {
  name: string
  setName: (v: string) => void
  postnumre: string
  setPostnumre: (v: string) => void
  saving: boolean
  onSave: () => void
  onCancel: () => void
}

function EditRow({ name, setName, postnumre, setPostnumre, saving, onSave, onCancel }: EditRowProps) {
  const { t } = useTranslation('components')
  return (
    <div className="p-3 border border-primary-300 dark:border-primary-700 rounded-lg space-y-2">
      <label className="block">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('mineMarkedsomrader.name')}
        </span>
        <input
          type="text" value={name} onChange={e => setName(e.target.value)}
          placeholder={t('mineMarkedsomrader.namePlaceholder')}
          className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-sm bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
        />
      </label>
      <label className="block">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('mineMarkedsomrader.postnummerLabel')}
        </span>
        <input
          type="text" value={postnumre} onChange={e => setPostnumre(e.target.value)}
          placeholder="0150,0151,0152"
          className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-sm bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100"
        />
      </label>
      <div className="flex gap-2">
        <button onClick={onSave} disabled={saving} className="btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" /> {saving ? t('mineMarkedsomrader.saving') : t('mineMarkedsomrader.save')}
        </button>
        <button onClick={onCancel} disabled={saving} className="btn-secondary flex items-center gap-2">
          <X className="w-4 h-4" /> {t('mineMarkedsomrader.cancel')}
        </button>
      </div>
    </div>
  )
}
